import { z } from 'zod';
import {
  ATTENDANCE_STATUS_VALUES,
  EXPORT_FORMAT_VALUES,
  LOAD_TYPE_VALUES,
  NOTIFICATION_EVENT_TYPE_VALUES,
  PAYROLL_PERIOD_STATUS_VALUES,
  REPORT_GROUP_BY_VALUES,
  SCHEDULED_MODALITY_VALUES,
  SESSION_TYPE_VALUES,
  TREND_INTERVAL_VALUES,
} from '@fas/types';
import { isoDateSchema, nonEmptyTrimmed, optionalText, paginationQuerySchema, uuidSchema } from './common';

/*
 * Verification, approval, payroll cut-offs, reports and notification preferences (Phase 1E,
 * §27, §29, §32–§36, §46.8, §46.9).
 */

const explanation = (label: string, min = 5) =>
  z
    .string(`${label} is required`)
    .trim()
    .min(min, `${label} must be at least ${min} characters`)
    .max(1000, `${label} must be at most 1000 characters`);

const sessionMinutes = z.number('Enter minutes as a whole number').int('Enter minutes as a whole number').min(0).max(1440, 'At most 1440 minutes');

const days = (from: string, to: string) => (Date.parse(to) - Date.parse(from)) / 86_400_000;

function refineRange(v: { from?: string; to?: string }, ctx: z.RefinementCtx, maxDays: number) {
  if (!v.from || !v.to) return;
  if (v.to < v.from) ctx.addIssue({ code: 'custom', path: ['to'], message: 'Must be on or after the start date' });
  else if (days(v.from, v.to) > maxDays) ctx.addIssue({ code: 'custom', path: ['to'], message: `The range cannot exceed ${maxDays + 1} days` });
}

// ── Session verification and approval (§27, §29.4, §29.5, §64) ─────────────────
export const startReviewSchema = z.object({ note: optionalText(1000) });
export type StartReviewInput = z.infer<typeof startReviewSchema>;

export const flagResolutionSchema = z.object({
  flagId: uuidSchema,
  resolution: z.enum(['RESOLVED', 'DISMISSED']),
  note: optionalText(500),
});

export const verifySessionSchema = z
  .object({
    /** Overrides the computed verified minutes; requires an explanation. */
    verifiedMinutes: sessionMinutes.optional(),
    note: optionalText(1000),
    /** Per-flag outcome; open flags not listed are resolved with the note. */
    flags: z.array(flagResolutionSchema).max(50).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.verifiedMinutes !== undefined && (!v.note || v.note.length < 5)) {
      ctx.addIssue({ code: 'custom', path: ['note'], message: 'Explain the adjusted minutes (at least 5 characters)' });
    }
  });
export type VerifySessionInput = z.infer<typeof verifySessionSchema>;

export const rejectSessionSchema = z.object({ reason: explanation('Reason') });
export type RejectSessionInput = z.infer<typeof rejectSessionSchema>;

export const returnSessionSchema = z.object({ reason: explanation('Reason') });
export type ReturnSessionInput = z.infer<typeof returnSessionSchema>;

export const approveSessionSchema = z.object({
  /** Required when the policy needs a manual value; otherwise overrides the computed value (with a note). */
  approvedPayableMinutes: sessionMinutes.optional(),
  note: optionalText(1000),
});
export type ApproveSessionInput = z.infer<typeof approveSessionSchema>;

export const bulkSessionActionSchema = z.object({
  sessionIds: z
    .array(uuidSchema)
    .min(1, 'Select at least one session')
    .max(500, 'At most 500 sessions at a time')
    .refine((ids) => new Set(ids).size === ids.length, 'Sessions must not repeat'),
  note: optionalText(1000),
});
export type BulkSessionActionInput = z.infer<typeof bulkSessionActionSchema>;

export const reviewQueueQuerySchema = paginationQuerySchema
  .extend({
    /** verification: sessions to verify · approval: verified sessions awaiting approval. */
    view: z.enum(['verification', 'approval']).default('verification'),
    attendanceStatus: z.enum(ATTENDANCE_STATUS_VALUES).optional(),
    periodId: uuidSchema.optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    departmentId: uuidSchema.optional(),
    collegeId: uuidSchema.optional(),
    facultyId: uuidSchema.optional(),
    sessionType: z.enum(SESSION_TYPE_VALUES).optional(),
  })
  .superRefine((v, ctx) => refineRange(v, ctx, 370));
export type ReviewQueueQuery = z.infer<typeof reviewQueueQuerySchema>;

// ── Payroll cut-offs (§32, §46.8) ──────────────────────────────────────────────
const periodFields = {
  name: nonEmptyTrimmed(100, 'Name'),
  startDate: isoDateSchema,
  endDate: isoDateSchema,
  /** Last day for approvals before HR finalization (§70 "approval deadline"). */
  submissionDeadline: isoDateSchema.nullable().optional(),
  notes: optionalText(1000),
};

function refinePeriod(v: { startDate?: string; endDate?: string; submissionDeadline?: string | null }, ctx: z.RefinementCtx) {
  if (v.startDate && v.endDate) {
    if (v.endDate < v.startDate) ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'Must be on or after the start date' });
    else if (days(v.startDate, v.endDate) > 61) ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'A cut-off cannot be longer than 62 days' });
  }
  if (v.submissionDeadline && v.startDate && v.submissionDeadline < v.startDate) {
    ctx.addIssue({ code: 'custom', path: ['submissionDeadline'], message: 'Must be on or after the start date' });
  }
  if (v.submissionDeadline && v.endDate && days(v.endDate, v.submissionDeadline) > 31) {
    ctx.addIssue({ code: 'custom', path: ['submissionDeadline'], message: 'Must be within 31 days after the end date' });
  }
}

export const createPayrollPeriodSchema = z.object(periodFields).superRefine(refinePeriod);
export type CreatePayrollPeriodInput = z.infer<typeof createPayrollPeriodSchema>;

export const updatePayrollPeriodSchema = z.object(periodFields).partial().superRefine(refinePeriod);
export type UpdatePayrollPeriodInput = z.infer<typeof updatePayrollPeriodSchema>;

export const payrollPeriodNoteSchema = z.object({ note: optionalText(1000) });
export type PayrollPeriodNoteInput = z.infer<typeof payrollPeriodNoteSchema>;

export const finalizePayrollPeriodSchema = z
  .object({
    /** Finalize although some sessions or requests are unresolved; they stay out of the payroll-ready totals. */
    acknowledgeUnresolved: z.boolean().optional(),
    reason: optionalText(1000),
    note: optionalText(1000),
  })
  .superRefine((v, ctx) => {
    if (v.acknowledgeUnresolved && (!v.reason || v.reason.length < 10)) {
      ctx.addIssue({ code: 'custom', path: ['reason'], message: 'Explain why unresolved items are left out (at least 10 characters)' });
    }
  });
export type FinalizePayrollPeriodInput = z.infer<typeof finalizePayrollPeriodSchema>;

export const reopenPayrollPeriodSchema = z.object({ reason: explanation('Reason', 10) });
export type ReopenPayrollPeriodInput = z.infer<typeof reopenPayrollPeriodSchema>;

export const markProcessedSchema = z.object({
  payrollReference: nonEmptyTrimmed(100, 'Payroll reference'),
  note: optionalText(1000),
  /**
   * The report version that was processed (Phase 3B): refused when the cut-off was reopened and
   * locked again since, so payroll never confirms a superseded report.
   */
  reportVersion: z.number().int().min(1).optional(),
  /** `records_sha256` of the hand-off that was imported; must match the current version's. */
  recordsSha256: z
    .string()
    .regex(/^[0-9a-f]{64}$/i, 'Must be a SHA-256 in hex')
    .transform((v) => v.toLowerCase())
    .optional(),
});
export type MarkProcessedInput = z.infer<typeof markProcessedSchema>;

export const approveDepartmentSchema = z.object({
  departmentId: uuidSchema.optional(),
  collegeId: uuidSchema.optional(),
  note: optionalText(1000),
});
export type ApproveDepartmentInput = z.infer<typeof approveDepartmentSchema>;

export const listPayrollPeriodsQuerySchema = paginationQuerySchema.extend({
  status: z.enum(PAYROLL_PERIOD_STATUS_VALUES).optional(),
  year: z.coerce.number().int().min(2000).max(2100).optional(),
});
export type ListPayrollPeriodsQuery = z.infer<typeof listPayrollPeriodsQuerySchema>;

// ── Reports and exports (§33, §35, §46.9) ──────────────────────────────────────
export const exportFormatSchema = z.object({ format: z.enum(EXPORT_FORMAT_VALUES, 'Choose xlsx, csv, pdf or html') });
export type ExportFormatQuery = z.infer<typeof exportFormatSchema>;

/** The table of a multi-table report for a CSV export (the report checks the id). */
export const exportTableSchema = z.object({ table: z.string().trim().regex(/^[a-z0-9-]{1,40}$/, 'Unknown table').optional() });
export type ExportTableQuery = z.infer<typeof exportTableSchema>;

/** §35 report filters (date range and cut-off are the report's own `from`/`to`). */
const reportFilters = {
  departmentId: uuidSchema.optional(),
  collegeId: uuidSchema.optional(),
  facultyId: uuidSchema.optional(),
  employmentTypeCode: z.string().trim().min(1).max(30).optional(),
  /** Subject. */
  courseId: uuidSchema.optional(),
  /** Semester. */
  termId: uuidSchema.optional(),
  academicYearId: uuidSchema.optional(),
  sessionType: z.enum(SESSION_TYPE_VALUES).optional(),
  loadType: z.enum(LOAD_TYPE_VALUES).optional(),
  scheduledModality: z.enum(SCHEDULED_MODALITY_VALUES).optional(),
};

export const attendanceReportQuerySchema = z
  .object({
    from: isoDateSchema,
    to: isoDateSchema,
    groupBy: z.enum(REPORT_GROUP_BY_VALUES).default('date'),
    attendanceStatus: z.enum(ATTENDANCE_STATUS_VALUES).optional(),
    ...reportFilters,
  })
  .superRefine((v, ctx) => refineRange(v, ctx, 369));
export type AttendanceReportQuery = z.infer<typeof attendanceReportQuerySchema>;

const sessionReportShape = { from: isoDateSchema, to: isoDateSchema, ...reportFilters };

export const sessionReportQuerySchema = z.object(sessionReportShape).superRefine((v, ctx) => refineRange(v, ctx, 369));
export type SessionReportQuery = z.infer<typeof sessionReportQuerySchema>;

/** Exception reports; weekday and starting hour narrow them to one cell of the tardiness pattern (Phase 2H). */
export const exceptionReportQuerySchema = z
  .object({
    ...sessionReportShape,
    /** ISO weekday of the scheduled start, 1 = Monday. */
    weekday: z.coerce.number().int().min(1).max(7).optional(),
    /** Hour of the scheduled start in the institution time zone. */
    hour: z.coerce.number().int().min(0).max(23).optional(),
  })
  .superRefine((v, ctx) => refineRange(v, ctx, 369));
export type ExceptionReportQuery = z.infer<typeof exceptionReportQuerySchema>;

/** Trends over weeks, months or semesters (Phase 2H, ADR-053). */
export const trendsReportQuerySchema = z
  .object({ ...sessionReportShape, interval: z.enum(TREND_INTERVAL_VALUES).default('week') })
  .superRefine((v, ctx) => refineRange(v, ctx, 369));
export type TrendsReportQuery = z.infer<typeof trendsReportQuerySchema>;

export const payrollReportQuerySchema = z.object({
  periodId: uuidSchema,
  /** A locked report version; defaults to the latest. */
  version: z.coerce.number().int().min(1).optional(),
});
export type PayrollReportQuery = z.infer<typeof payrollReportQuerySchema>;

/** `GET /payroll-periods/:id/handoff?version=` — the §69 hand-off (Phase 3B); default the current version. */
export const payrollHandoffQuerySchema = z.object({ version: z.coerce.number().int().min(1).optional() }).strict();
export type PayrollHandoffQuery = z.infer<typeof payrollHandoffQuerySchema>;

export const payrollHandoffExportSchema = z
  .object({
    format: z.enum(['csv', 'xlsx'], 'Choose csv or xlsx'),
    version: z.coerce.number().int().min(1).optional(),
  })
  .strict();
export type PayrollHandoffExportQuery = z.infer<typeof payrollHandoffExportSchema>;

// ── Notification preferences (§36) ─────────────────────────────────────────────
export const updateNotificationPreferencesSchema = z.object({
  preferences: z
    .array(z.object({ eventType: z.enum(NOTIFICATION_EVENT_TYPE_VALUES), inApp: z.boolean(), email: z.boolean(), push: z.boolean().optional(), sms: z.boolean().optional() }))
    .min(1)
    .max(NOTIFICATION_EVENT_TYPE_VALUES.length)
    .refine((list) => new Set(list.map((p) => p.eventType)).size === list.length, 'Each notification type may appear once'),
});
export type UpdateNotificationPreferencesInput = z.infer<typeof updateNotificationPreferencesSchema>;
